import {
  ADD_TO_BASKET,
  ANALYTICS_JLDATA_UPDATED,
  PAGE_LOAD,
} from "@/utils/events";
import { useEffect, useRef, useState } from "react";
import { setJLData } from "@/utils/setJLData";
import { useFeatureFlags } from "@/flags/flags";
import { RouteParams, useRouteParams } from "@/hooks/useRouteParams";
import { useRouter } from "next/router";
import { useWindowSize } from "@/hooks/useWindowSize";
import { analyticsLogger } from "@/utils/clientLogger";
import { isAlsoSearched, setAlsoSearched } from "@/utils/alsoSearched";
import {
  dispatchAdTechAnalyticsEvent,
  dispatchAnalyticsEvent,
} from "@~/utils/analytics";
import { JL_EVENTS_ADD_TO_BASKET_SUCCESS } from "@~/utils/events";
import {
  ProductListingData,
  ProductListingProduct,
} from "@/types/productListing";
import { PageProps } from "@/getServerSideProps/getServerSideProps";
import { setCustomAttribute } from "@/utils/newrelic";
import { useConstructorListingAnalytics } from "@/hooks/useConstructorListingAnalytics";
import { productCardEvents } from "@~/contexts/ProductCardEventContext/productCardEvents";

export function Analytics({
  productListingData,
  pageConfig,
  productListProps,
  foundItData,
}: Pick<
  PageProps,
  "productListingData" | "pageConfig" | "productListProps" | "foundItData"
>) {
  const flags = useFeatureFlags();
  const route = useRouteParams();
  const router = useRouter();
  const [initialPageLoad, setInitialPageLoad] = useState(true);
  const { width: windowWidth } = useWindowSize();

  const productListConfig = productListProps.config;

  useConstructorListingAnalytics(productListingData, route);

  const { previousProductListingData } = usePreviousData(
    productListingData,
    route,
  );

  useEffect(
    function updateJlDataAndDispatchPageLoad() {
      if (productListingData !== previousProductListingData) {
        analyticsLogger.info("Analytics: useEffect: updating jlData...");
        const jlData = setJLData({
          productListingData,
          flags,
          route,
          router,
          windowWidth: windowWidth || 0,
          hasUserClickedSimilarSearch: isAlsoSearched(),
          foundItData,
        });
        analyticsLogger.info("Analytics: useEffect: jlData", jlData);
        analyticsLogger.info(
          `Analytics: useEffect: dispatching ${ANALYTICS_JLDATA_UPDATED} event`,
        );
        document.body.dispatchEvent(new CustomEvent(ANALYTICS_JLDATA_UPDATED));

        setAlsoSearched(false);

        const hashParams = new URLSearchParams(
          router.asPath.split("#")[1] ?? "",
        );
        const isFoundItNavigation = hashParams.get("fi") === "c";

        if (initialPageLoad) {
          dispatchAnalyticsEvent(
            PAGE_LOAD,
            isFoundItNavigation ? { reason: "foundIt navigation" } : undefined,
          );
          setCustomAttribute("pageConfig", pageConfig.name);
          setCustomAttribute(
            "pageConfigSelectionMethod",
            `${pageConfig.selectionMethod}`,
          );
          setCustomAttribute(
            "productListConfig",
            productListConfig.name ?? "unknown",
          );
          setCustomAttribute(
            "productListConfigSelectionMethod",
            `${productListConfig.selectionMethod}`,
          );
          setInitialPageLoad(false);
        } else if (isFoundItNavigation) {
          dispatchAnalyticsEvent(PAGE_LOAD, { reason: "foundIt navigation" });
        }
      }
    },
    [
      productListingData,
      flags,
      route,
      router,
      windowWidth,
      previousProductListingData,
      initialPageLoad,
      pageConfig.name,
      pageConfig.selectionMethod,
      productListConfig.name,
      productListConfig.selectionMethod,
      foundItData,
    ],
  );

  useEffect(function listenForAdTechClicks() {
    const adTechClickEvents = [
      productCardEvents.cardClick,
      productCardEvents.colourSwatchClicked,
      productCardEvents.energyRatingClicked,
      productCardEvents.promoMessagesViewed,
      productCardEvents.quickViewOpened,
    ];

    const onAdTechAnalytics = (event: Event) => {
      const customEvent = event as CustomEvent<{
        pimProductId?: string;
        product?: ProductListingProduct;
        originator?: string;
      }>;
      const { detail } = customEvent;

      // Don't fire two AdTech events (promoMessagesViewed and quickViewOpened) for the same click on "View offers" button
      if (
        customEvent.type === productCardEvents.quickViewOpened &&
        detail.originator === "VIEW_OFFERS"
      ) {
        return;
      }

      const { pimProductId, product } = detail;
      const advertising = product?.advertising;

      if (advertising?.associatedAdvertId) {
        dispatchAdTechAnalyticsEvent({
          productId: pimProductId ?? "",
          associatedAdvertId: advertising.associatedAdvertId,
          clickUrl: advertising.clickUrl,
          impressionUrl: advertising.impressionUrl,
        });
      }
    };

    adTechClickEvents.forEach((eventName) => {
      document.body.addEventListener(eventName, onAdTechAnalytics);
    });

    return () => {
      adTechClickEvents.forEach((eventName) => {
        document.body.removeEventListener(eventName, onAdTechAnalytics);
      });
    };
  }, []);

  useEffect(function listenForAddToBasketSuccess() {
    const onAddToBasketSuccess = () => {
      dispatchAnalyticsEvent(ADD_TO_BASKET);
    };

    document.body.addEventListener(
      JL_EVENTS_ADD_TO_BASKET_SUCCESS,
      onAddToBasketSuccess,
    );

    return () => {
      document.body.removeEventListener(
        JL_EVENTS_ADD_TO_BASKET_SUCCESS,
        onAddToBasketSuccess,
      );
    };
  }, []);

  return null;
}

const usePreviousData = (
  productListingData: ProductListingData,
  route: RouteParams,
) => {
  const productListingDataRef = useRef<ProductListingData | undefined>(
    undefined,
  );
  const routeRef = useRef<RouteParams | undefined>(undefined);

  useEffect(
    function storePreviousDataRefs() {
      if (productListingDataRef.current !== productListingData) {
        productListingDataRef.current = productListingData;
        routeRef.current = route;
      }
    },
    [productListingData, route],
  );

  return {
    previousProductListingData: productListingDataRef.current,
    previousRoute: routeRef.current,
  };
};
